import type { FC } from 'react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { useIsMobile } from 'hooks/useMedia';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import { getOfferTranslation, setShowTranslation } from '@Modules/offers/slice';
import { generateCategoryLink } from '@Modules/categories/utils';
import { selectLanguage, selectRegionUri } from '@Modules/app/selectors';
import { selectOffersShowTranslation, selectOffersSimilar } from '@Modules/offers/selectors';
import type { OfferCardType, OfferMarkupType, OfferType } from '@Modules/offers/types';

import { Layout } from '@Atomic/templates/Layout';
import { Page } from '@Atomic/templates/Page';
import { CategoryRedirectCard } from '@Atomic/organisms/offer/CategoryRedirectCard';
import { ClosedOfferCard } from '@Atomic/organisms/offer/ClosedOfferCard';
import { OfferDescription } from '@Atomic/organisms/offer/OfferDescription';
import { OfferFeatures } from '@Atomic/organisms/offer/OfferFeatures';
import { OfferSimilarItems } from '@Atomic/organisms/offer/OfferSimilarItems';
import { OfferTop } from '@Atomic/organisms/offer/OfferTop';
import { Container } from '@Atomic/molecules/Grid';
import { OfferCardBanner } from '@Atomic/molecules/OfferCardBanner';
import { MetaCanonical } from '@Atomic/molecules/opengraph/MetaCanonical';
import type { BreadcrumbType } from '@Atomic/atoms/Breadcrumbs';
import { Breadcrumbs } from '@Atomic/atoms/Breadcrumbs';
import { Button } from '@Atomic/atoms/Button';
import { Title } from '@Atomic/atoms/Headline';
import { Icons } from '@Atomic/atoms/Icons';
import { Message } from '@Atomic/atoms/message';

import styles from './closedOfferContent.module.scss';

type Props = WithTestId<{
  offerCard: OfferCardType;
  markup: OfferMarkupType | null;
  breadcrumbs: BreadcrumbType[];
  onGoBack: () => void;
}>;

type ExpandedSectionsProps = {
  offer: OfferType;
  testId: string;
  panelId: string;
  hasFeatures: boolean;
  showTranslation: boolean;
  offerTranslation: OfferCardType['translation'] | undefined;
  onTranslate: () => void;
  onShowOriginal: () => void;
};

const ExpandedSections: FC<ExpandedSectionsProps> = ({
  offer,
  testId,
  panelId,
  hasFeatures,
  showTranslation,
  offerTranslation,
  onTranslate,
  onShowOriginal,
}) => {
  const { t } = useTranslation();

  return (
    <div
      id={panelId}
      className={styles.expandedSections}
      data-testid={getTestIdByParts(testId, 'expanded')}
    >
      {hasFeatures && (
        <section className={styles.section}>
          <Title.H2 className={styles.sectionTitle}>{t('offer.features')}</Title.H2>
          <OfferFeatures
            className={styles.offerFeatures}
            key={offer.id}
            features={offer.features}
            testId={getTestIdByParts(testId, 'features')}
          />
        </section>
      )}

      {offer.description && (
        <section className={styles.section}>
          <Title.H2 className={styles.sectionTitle}>{t('offer.description')}</Title.H2>
          <OfferDescription
            className={styles.offerDescription}
            testId={getTestIdByParts(testId, 'description')}
            translationAvailable={offer.translationAvailable}
            showTranslation={showTranslation}
            onShowOriginal={onShowOriginal}
            onTranslate={onTranslate}
            description={offerTranslation?.description || offer.description}
          />
        </section>
      )}
    </div>
  );
};

export const ClosedOfferContent: FC<Props> = ({
  offerCard,
  markup,
  breadcrumbs,
  onGoBack,
  testId = 'offer-closed',
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const offer = offerCard.card;
  const language = useSelector(selectLanguage);
  const region = useSelector(selectRegionUri);
  const similar = useSelector(selectOffersSimilar);
  const showTranslation = useSelector(selectOffersShowTranslation);

  const [isExpanded, setIsExpanded] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const reactId = useId();
  const panelId = `closed-offer-expanded-${reactId}`;

  if (!offer) {
    return null;
  }

  const offerTranslation = showTranslation ? offerCard.translation : undefined;

  const categoryLeaf = markup?.breadcrumbs?.[markup.breadcrumbs.length - 1];
  const categoryHref = categoryLeaf
    ? generateCategoryLink({ uri: categoryLeaf.webUriInfo[language] })
    : `/${language}/${region}`;

  const handleShowTranslationAction = (value: boolean) => {
    dispatch(setShowTranslation(value));
  };

  const handleTranslate = () => {
    if (offerCard.translation) {
      handleShowTranslationAction(true);

      return;
    }

    dispatch(
      getOfferTranslation.request(
        { offerId: offer.id },
        { onSuccess: () => handleShowTranslationAction(true) }
      )
    );
  };

  const hasFeatures = !!offer.features.length;
  const hasSimilar = !!similar.data.length;
  const canExpand = hasFeatures || !!offer.description;

  const toggleButton = canExpand ? (
    <Button
      className={cn(styles.toggleButton, { [styles.toggleButtonOpen]: isExpanded })}
      variant="text"
      intent="info"
      size="large"
      onClick={() => setIsExpanded((prev) => !prev)}
      iconRight={<Icons.ChevronDown className={styles.toggleIcon} aria-hidden="true" />}
      testId={getTestIdByParts(testId, 'toggle')}
      aria-expanded={isExpanded}
      aria-controls={panelId}
    >
      {isExpanded ? t('offer.closed.hideFull') : t('offer.closed.showFull')}
    </Button>
  ) : null;

  const expandedSections = isExpanded ? (
    <ExpandedSections
      offer={offer}
      testId={testId}
      panelId={panelId}
      hasFeatures={hasFeatures}
      showTranslation={showTranslation}
      offerTranslation={offerTranslation}
      onTranslate={handleTranslate}
      onShowOriginal={() => handleShowTranslationAction(false)}
    />
  ) : null;

  const warning = (
    <Message severity="warning" hideIcon className={styles.alert}>
      {t('offer.expiredOfferMessage')}
    </Message>
  );

  return (
    <Layout
      fixedHeaderOnScroll
      showChatWidget
      hasScrollToTop
      hasSearch
      footerHasOffset={false}
      fixedAnchorRef={anchorRef}
      fixedContentOnScroll={
        <OfferTop offer={offer} isExpired withContacts={false} onGoBack={onGoBack} />
      }
      // Passed unconditionally: TopActionsBar owns the mobile-only gate (BP-8082).
      onActionsBarBack={onGoBack}
    >
      {/* Closed offers must not compete with category/filter pages in search results,
          so the canonical points to the page the old redirect used to lead to (BP-7512) */}
      {categoryLeaf && <MetaCanonical canonical={categoryHref} />}
      <Page
        onGoBack={isMobile ? undefined : onGoBack}
        breadcrumbs={isMobile ? undefined : breadcrumbs}
      >
        <div ref={anchorRef} className={styles.anchor} />
        <Container className={styles.root} data-testid={testId}>
          {/* Phones get the crumbs in the white header under the actions bar, not in Page (BP-8728) */}
          {isMobile && (
            <Breadcrumbs
              className={styles.mobileBreadcrumbs}
              breadcrumbs={breadcrumbs}
              testId="breadcrumbs"
            />
          )}

          {!isMobile && warning}

          <div className={styles.headerStack}>
            <ClosedOfferCard
              offer={offer}
              testId={getTestIdByParts(testId, 'card')}
              inlineToggle={isMobile ? toggleButton : undefined}
              warning={warning}
            />

            {isMobile ? expandedSections : null}

            <CategoryRedirectCard
              categoryHref={categoryHref}
              categoryName={categoryLeaf?.title}
              categoryImage={offerCard.relatedCategory?.image}
              testId={getTestIdByParts(testId, 'category')}
            />
          </div>

          {!isMobile && toggleButton ? (
            <div className={styles.toggleWrapper}>{toggleButton}</div>
          ) : null}

          {!isMobile ? expandedSections : null}

          {hasSimilar && (
            <section className={cn(styles.section, styles.similar)}>
              <Title.H3 className={styles.sectionTitle}>{t('offer.similar')}</Title.H3>
              <OfferSimilarItems
                offerId={offer.id}
                className={styles.similarFeed}
                testId={testId}
              />
            </section>
          )}

          <section className={cn(styles.section, styles.bannerSection)}>
            <OfferCardBanner testId={getTestIdByParts(testId, 'banner')} />
          </section>
        </Container>
      </Page>
    </Layout>
  );
};
